import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import { RootState } from "@store";
import { IModificationsClient, ModificationsClient } from "app/ApiClient";
import { LoadingState } from "types";
import { getProjectFileName } from "utils";
import { tryGetValueFromJson } from "utils/parseHelper";
import { initialInstructionsState } from "./instructionsState";

const modificationClient: IModificationsClient = new ModificationsClient(process.env.REACT_APP_API_URL);

export const pushInstructions = createAsyncThunk(
  "instructions/push",
  async (modificationId: number, { rejectWithValue }) => {
    try {
      const result = await modificationClient.pushInstructions(modificationId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const isValidFileName = (fileName: string) => {
  return /^[\w,\s-]/.test(fileName);
};
export const downloadInstructions = createAsyncThunk(
  "instructions/download",
  async (
    { modificationName, modificationId }: { modificationId: number; modificationName: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { currentVersionId },
      } = getState() as RootState;
      const response = await modificationClient.getInstructionsJson(modificationId);
      saveAs(
        response.data,
        getProjectFileName(
          String(currentProject?.originProjectId),
          String(currentProject?.name),
          String(currentVersionId),
          "Modified",
          ".json",
          isValidFileName(modificationName) ? modificationName : "instructions",
        ),
      );
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const instructionsSlice = createSlice({
  name: "instructions",
  initialState: initialInstructionsState,
  reducers: {
    resetInstructions: () => initialInstructionsState,
  },
  extraReducers: (builder) => {
    // pushInstructions
    builder.addCase(pushInstructions.pending, (state) => {
      state.pushLoading = LoadingState.pending;
      state.pushResult = null;
      state.pushErrorMessage = undefined;
    });
    builder.addCase(pushInstructions.fulfilled, (state, { payload }) => {
      state.pushLoading = LoadingState.succeed;
      state.pushResult = payload;
      state.pushErrorMessage = undefined;
    });
    builder.addCase(pushInstructions.rejected, (state, { error: { message } }) => {
      state.pushLoading = LoadingState.failed;
      state.pushResult = null;
      state.pushErrorMessage = tryGetValueFromJson(message, "detail");
    });

    // downloadInstructions
    builder.addCase(downloadInstructions.pending, (state) => {
      state.downloadLoading = LoadingState.pending;
      state.downloadResult = null;
      state.downloadErrorMessage = undefined;
    });
    builder.addCase(downloadInstructions.fulfilled, (state, { payload }) => {
      state.downloadLoading = LoadingState.succeed;
      state.downloadResult = payload;
      state.downloadErrorMessage = undefined;
    });
    builder.addCase(downloadInstructions.rejected, (state, { error: { message } }) => {
      state.downloadLoading = LoadingState.failed;
      state.downloadResult = null;
      state.downloadErrorMessage = tryGetValueFromJson(message, "detail");
    });
  },
});
export const { resetInstructions } = instructionsSlice.actions;
export default instructionsSlice.reducer;
